/* eslint-disable @typescript-eslint/no-empty-function */
import { createContext, useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';

import { AuthApi, ClinicsApi, UsersApi } from '../api/client';
import { ROUTES } from '../constants/routes';
import { RefreshTokenData } from '../models';
import { Loader } from '../shared/components';
import { Account, Clinic } from '../shared/types';

type ContextProps = {
  isAuth: boolean;
  me: Account | undefined;
  onLogin: (token: string, refreshToken: string) => void;
  onLogout: () => void;
};
type AuthState = {
  isAuth: boolean;
  me?: Account;
  token?: string;
  refreshToken?: string;
};
type AuthProviderProps = {
  children?: JSX.Element;
};

export const authContext = createContext<ContextProps>({
  isAuth: false,
  me: undefined,
  onLogin: () => {},
  onLogout: () => {},
});

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [auth, setAuth] = useState<AuthState>({
    isAuth: false,
  });

  const { data: clinics } = useQuery<Clinic[]>(
    'CLINICS',
    ClinicsApi.fetchClinics,
    {
      onError: (err) => {
        console.log(err);
      },
    }
  );

  useQuery<Account>(
    ['MY_ACCOUNT', auth.token, clinics],
    UsersApi.fetchMyAccount,
    {
      onSuccess: (myAccount: Account) => {
        setAuth({
          ...auth,
          me: {
            ...myAccount,
            clinicName:
              clinics &&
              clinics.find((clinic) => clinic.id === myAccount?.clinicId)?.name,
          },
        });
      },
      onError: (err) => {
        console.log(err);
      },
    }
  );

  const onLogin = (token: string, refreshToken: string) => {
    setAuth({
      isAuth: true,
      token,
      refreshToken,
    });
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
    handelRefreshToken();
  };

  const onLogout = () => {
    setAuth({
      isAuth: false,
      me: undefined,
      token: '',
      refreshToken: '',
    });
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    history.push(ROUTES.LOGIN);
  };

  const refreshTokenMutation = useMutation(
    (data: RefreshTokenData) => AuthApi.refreshToken(data),
    {
      onSuccess: (res) => {
        const { token, refreshToken } = res.data;
        onLogin(token, refreshToken);
      },
      onError: () => {
        setAuth({
          isAuth: false,
        });
      },
      onSettled: () => {
        setIsLoading(false);
      },
    }
  );

  const handelRefreshToken = () => {
    const oneDayInSec = 3600 * 24;
    const oneWeekInSec = oneDayInSec * 7;
    const refreshTiming = oneWeekInSec * 1000;
    const handelRefreshToken = async () => {
      const { refreshToken } = auth;
      if (refreshToken) {
        refreshTokenMutation.mutate({
          refreshToken,
        });
      }
    };
    setTimeout(handelRefreshToken, refreshTiming);
  };

  useEffect(() => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      refreshTokenMutation.mutate({ refreshToken });
    } else {
      setIsLoading(false);
    }
  }, []);

  if (isLoading) {
    return <Loader />;
  }
  return (
    <authContext.Provider
      value={{ isAuth: auth.isAuth, onLogin, onLogout, me: auth.me }}>
      {children}
    </authContext.Provider>
  );
};
